/**
 * Authentication saga
 */

import { call, all, put, select, takeEvery, takeLatest, cancelled } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

import {
  USER_LOGIN,
  USER_CHECK,
  USER_LOGOUT,
  FETCH_ERROR,
  LOAD_USER,
  CANCEL_CURRENT_PLAN,
} from 'containers/App/constants';
import {
  userLogout,
  userLoginSuccess,
  userLoginError,
  loadUser,
  loadUserError,
  loadUserSuccess,
  loadUserCancelled,
  cancelCurrentPlan,
} from 'containers/App/actions';
import { makeSelectLocation, makeSelectNextPathname, makeSelectUser } from 'containers/App/selectors';
import history from 'utils/history';
import { tokenDataKey, tokenKey } from 'utils/authProvider';
import request from 'utils/request';
import isJsonString from 'utils/checkJsonString';
import { getWithExpiry, setWithExpiry } from 'utils/expiryHandler';

export default authProvider => {
  if (!authProvider) {
    return () => null;
  }

  function* handleAuth(action) {
    const { type, payload, error, meta = {} } = action;
    switch (type) {
      case LOAD_USER: {
        try {
          const userDetails = yield call(authProvider.getUserDetails);
          yield put(loadUserSuccess(userDetails));
        } catch (err) {
          yield put(loadUserError(err));
        } finally {
          if (yield cancelled()) {
            yield put(loadUserCancelled());
          }
        }
        break;
      }
      case USER_LOGIN: {
        try {
          const authResp = yield call(authProvider.login, payload);
          yield put(userLoginSuccess(authResp));
          const redirectTo = yield meta.pathName || select(makeSelectNextPathname());
          yield put(push(redirectTo || '/'));
        } catch (err) {
          yield put(userLoginError(err));
        }
        break;
      }
      case USER_CHECK: {
        try {
          yield call(authProvider.checkAuth, payload);
          const user = yield select(makeSelectUser());
          if (!user || user.fromCache) {
            yield put(loadUser());
          }
          const tokenData = authProvider.getTokenData();
          const redirectTo = yield meta.pathName || select(makeSelectNextPathname());
          const subscriptionWaiting = getWithExpiry('subscription-waiting');
          const timeWaiting = 1000 * 60 * 60 * 24; // 1 day
          if (
            tokenData &&
            tokenData.role !== 'superadmin' &&
            !String(redirectTo).includes('/subscription') &&
            !subscriptionWaiting
          ) {
            const requestURL = `/stripe/contact-us-plan/check`;
            const plan = yield call(request, requestURL);

            if (plan.json) {
              const data = plan.json;
              if (data.current_plan) {
                if (data.custom_plan) {
                  const customPlanConfirm = yield Swal.fire({
                    title: 'Warning!',
                    text:
                      'We see you requested to sign up for enterprise plan but you already subscribed to another plan so you have to cancel it before moving to new plan.',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Cancel current plan',
                    cancelButtonText: 'Pause for 1 day',
                    buttonsStyling: false,
                    allowOutsideClick: false,
                    customClass: {
                      confirmButton: 'btn btn-danger btn-lg m-swal',
                      cancelButton: 'btn btn-secondary btn-lg m-swal',
                    },
                  });
                  if (customPlanConfirm.isConfirmed) {
                    yield put(cancelCurrentPlan());
                    yield put(
                      push({
                        pathname: `/subscription/${data.custom_plan.price_id}/checkout`,
                        state: { ...data.custom_plan, customPlan: true },
                      }),
                    );
                  }
                  if (customPlanConfirm.isDismissed) {
                    setWithExpiry('subscription-waiting', data.custom_plan, timeWaiting);
                  }
                  return;
                }
                return;
              } else {
                if (data.custom_plan) {
                  return yield put(
                    push({
                      pathname: `/subscription/${data.custom_plan.price_id}/checkout`,
                      state: { ...data.custom_plan, customPlan: true },
                    }),
                  );
                }
                return yield Swal.fire({
                  title: 'Welcome',
                  text: 'Before you can start using Parousya SaaS portal, you need to get a subscription.',
                  icon: 'warning',
                  buttonsStyling: false,
                  showCancelButton: true,
                  customClass: {
                    confirmButton: 'btn btn-primary btn-lg m-swal',
                    cancelButton: 'btn btn-danger btn-lg m-swal',
                    loader: 'custom-loader',
                  },
                  allowOutsideClick: false,
                  confirmButtonText: 'Continue',
                  cancelButtonText: 'Cancel',
                }).then(swal => {
                  localStorage.removeItem('NOT_REGISTERED_PACKAGE');
                  if (swal.isConfirmed) {
                    history.replace('/subscription');
                  } else if (swal.isDismissed) {
                    localStorage.removeItem(tokenKey);
                    localStorage.removeItem(tokenDataKey);
                    history.replace('/login');
                    window.location.reload();
                  }
                });
              }
            }
          }
        } catch (e) {
          yield put(
            userLogout(
              {
                redirectTo: (e && e.redirectTo) || undefined,
              },
              meta.pathName,
            ),
          );
          yield call(toast.error, 'Your session has expired. Please login again.');
        }
        break;
      }
      case CANCEL_CURRENT_PLAN: {
        let requestURL = `/stripe/cancel-subscription`;
        const requestOpts = {
          method: 'DELETE',
        };

        try {
          const resp = yield call(request, requestURL, requestOpts);
          if (resp.json) {
            yield toast.success('Cancel Successful');
          }
        } catch (err) {
          if (err.message) {
            if (!isJsonString(err.message)) {
              toast.error(err.message);
            }
          } else {
            toast.error(String(err));
          }
        }
        break;
      }
      case USER_LOGOUT: {
        yield call(authProvider.logout);
        yield put(
          push({
            pathname: (action.payload && action.payload.redirectTo) || '/login',
            state: { nextPathname: meta.pathName },
          }),
        );
        yield window.location.reload();
        break;
      }
      case FETCH_ERROR:
        try {
          yield call(authProvider.checkError, error);
        } catch (e) {
          //  stripe - Subscription
          if (e.message === 'NOT_REGISTERED_PACKAGE') {
            // if (!localStorage.getItem('NOT_REGISTERED_PACKAGE')) {
            //   localStorage.setItem('NOT_REGISTERED_PACKAGE', true);
            //   yield Swal.fire({
            //     title: 'Welcome',
            //     text: 'Before you can start using Parousya SaaS portal, you need to get a subscription.',
            //     icon: 'warning',
            //     buttonsStyling: false,
            //     showCancelButton: true,
            //     customClass: {
            //       confirmButton: 'btn btn-primary btn-lg m-swal',
            //       cancelButton: 'btn btn-danger btn-lg m-swal',
            //       loader: 'custom-loader',
            //     },
            //     allowOutsideClick: false,
            //     confirmButtonText: 'Continue',
            //     cancelButtonText: 'Cancel',
            //   }).then(swal => {
            //     localStorage.removeItem('NOT_REGISTERED_PACKAGE');
            //     if (swal.isConfirmed) {
            //       history.replace('/subscription');
            //     } else if (swal.isDismissed) {
            //       localStorage.removeItem(tokenKey);
            //       localStorage.removeItem(tokenDataKey);
            //       history.replace('/login');
            //       window.location.reload();
            //     }
            //   });
            // }
            return;
          } else if (e.message === '402') {
            return;
            // stripe - Subscription
          } else {
            localStorage.removeItem('NOT_REGISTERED_PACKAGE');
            const pathName = yield select(makeSelectLocation());
            yield put(userLogout({}, pathName));
          }
        }
        break;
      default:
        // do nothing
        break;
    }
  }
  return function* watchAuthActions() {
    yield all([
      takeEvery([USER_LOGIN, USER_CHECK, USER_LOGOUT], handleAuth),
      takeLatest(FETCH_ERROR, handleAuth),
      takeLatest(LOAD_USER, handleAuth),
      takeLatest(CANCEL_CURRENT_PLAN, handleAuth),
    ]);
  };
};
